import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { LoadingService } from '../loading.service';
import { GlobalsService } from '../globals.service';
import { PNRLookupAWSInt } from '@app/interfaces/reservation-aws.interface';
import { catchError, lastValueFrom, map, throwError } from 'rxjs';
import { PnrAWSMapper } from '@app/mappers/pnr-aws.mapper';
import { AdminService } from '../admin.service';
import { Reservation } from '@app/models/micro/reservation.model';
import moment from 'moment';
import { PassengerListStandbyInt } from '@app/interfaces/micro/passengerListStandby.interface';
import { ElegibilityRespInt } from '@app/interfaces/micro/passengerElegibility.interface';
import { ApiMapper } from '@app/mappers/micro/api.mapper';
import {
  ContactabilityDigitalInt,
  ContactabilityTokenInt,
} from '@app/interfaces/micro/contactability.interface';
import { CheckinPecTabInt } from '@app/interfaces/micro/checkinPecTab.interface';
import { TypeLoading } from '@app/enums';
import { AncillariesMapper } from '@app/mappers/micro/acillaries.mapper';
// src\app\mappers\micro\acillaries.mapper.ts
import { DocumentsMapper } from '@app/mappers/micro/documents.mapper';
import { ErrorAdapter } from '@models/adapter/error.adapter';
import { getReservationLookupErrorMsg, sanitizePnrInput } from '@utils/validations/pnr.validation';
import { throwPnrLookupError } from '@utils/functions/pnr-lookup-error';

type SearchCriterial = 'PNR' | 'TICKET' | 'AIRPORT' | 'CITY' | 'NUMBERFLIGHT' | 'SCANPASSPORT';

/**
 * Servicio para realizar llamadas a la API de check-in,
 * getViewDocuments,
 * UpdateDocuments,
 * eligibility,
 * passengerlistupgrade,
 * passengerliststandby,
 * digital,
 * getFlightDetails,
 * getBoardingPass,
 */
@Injectable({
  providedIn: 'root',
})
export class ApiService {
  // MyTrips: la respuesta llega cifrada y la descifra ApiEncryptionInterceptor (ver encrypted-endpoints).
  urlMicroPNR = `${environment.urlApiMyTrips}${environment.versionMyTrips}/kiosk/mytrips/reservation`;
  urlMiroSeamless: string = `${environment.urlApiCheckin}/tc/reservation${environment.versionSeamless}/kiosk/pnr`;
  urlApiMicroEligibility: string = `${environment.urlApiMicroEligibility}`;
  urlApiMicro: string = `${environment.urlApiMicro}`;
  urlApiContactabilityAuth: string = `${environment.urlApiContactabilityAuth}`;
  urlApiContactability: string = `${environment.urlApiContactability}`;
  constructor(
    private _loadingService: LoadingService,
    private _http: HttpClient,
    private _globalService: GlobalsService,
    private _admin: AdminService
  ) {}

  /**
   * Obtiene la reserva de un pasajero basándose en diferentes criterios de búsqueda.
   * @param {SearchCriterial} criteria - Criterio de búsqueda (PNR, Aeropuerto, etc.)
   * @param {string} [firstName] - Nombre del pasajero
   * @param {string} [lastName] - Apellido del pasajero
   * @param {string} [numberFlight] - Número de vuelo
   * @param {string} [arrivalCity] - Ciudad de llegada
   * @param {string} [arrivalAirport] - Aeropuerto de llegada
   * @param {string} [pnr] - Código de reserva (PNR)
   * @returns {Observable<PNRLookupAWSInt>} Datos de la reserva procesados por el mapeador
   */

  getReservation(
    criteria: SearchCriterial,
    firstName: string = null,
    lastName: string = null,
    numberFlight: string = null,
    arrivalCity: string = null,
    arrivalAirport: string = null,
    pnr: string = null
  ) {
    const { Location } = this._admin.admin;
    const sanitizedPnr = sanitizePnrInput(pnr);

    if (criteria === 'PNR' || criteria === 'TICKET') {
      const lookupError = getReservationLookupErrorMsg(sanitizedPnr, {
        criteria,
        departureAirport: Location,
        checkDepartureAirport: true,
      });
      if (lookupError) {
        return throwPnrLookupError(lookupError);
      }
    }

    let headers = new HttpHeaders({
      channel: environment.channel,
      'x-transactionId': '',
      searchCriteria: criteria,
      firstName: firstName || '',
      lastName: lastName || '',
      numberFlight: numberFlight || '',
      arrivalCity: arrivalCity || '',
      arrivalAirport: arrivalAirport || '',
      dateTime: moment().format('YYYY-MM-DDTHH:mm:ss'),
      pnr: sanitizedPnr,
      departureAirport: Location || '',
      // La versión del BFF ya no va en la URL; viaja en este header con formato 'v1'.
      versionBFF: (environment.versionSeamless || '').replace(/^\//, ''),
    });
    if (sanitizedPnr?.length == 13) {
      headers = headers.delete('pnr');
      headers = headers.set('ticket', sanitizedPnr);
    }

    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this._http.get<PNRLookupAWSInt>(this.urlMicroPNR, { headers }).pipe(
      map((res) => {
        this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
        this._globalService.setPNRLookupAWSInt(res);
        return PnrAWSMapper.pnrBuild(res, Location);
      }),
      catchError((error) => this.handlerError(error, TypeLoading.FOOTER_LOADING))
    );
  }

  /**
   * Obtiene la elegibilidad de un pasajero para el proceso de check-in.
   *
   * @param {PNRLookupAWSInt} reservation - Información de la reservación obtenida desde el sistema AWS.
   * @param {TypeLoading} typeLoading - Tipo de indicador de carga que se mostrará mientras se procesa la solicitud.
   * @param {Reservation} lookup - Información detallada de la reservación en el sistema interno.
   * @returns {Observable<ElegibilityRespInt>} - Un observable que emite la respuesta de elegibilidad del check-in.
   */

  getEligibility(reservation: PNRLookupAWSInt, typeLoading: TypeLoading, lookup: Reservation) {
    const body = ApiMapper.requestEligibility(reservation, lookup);
    let headers = new HttpHeaders({
      channel: environment.channel,
      flow: 'checkin',
      cacheEnabled: 'false',
      'x-transaction-id': reservation.transactionId,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    });
    this._loadingService.showLoading(typeLoading);
    return this._http
      .post<ElegibilityRespInt>(
        `${environment.urlApiCheckinBFF}${environment.versionCheckin}/kiosk/checkin${environment.versionEligibility}/eligibility`,
        body,
        { headers }
      )
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(typeLoading);

          return res as ElegibilityRespInt;
        }),
        catchError((error) => this.handlerError(error, typeLoading))
      );
  }

  /**
   * Obtiene la lista de pasajeros en espera.
   *
   * @param {Reservation} reservation - Información de la reservación del vuelo.
   * @returns {Observable<PassengerListStandbyInt>} - Un observable que emite la lista de pasajeros en espera para una mejora de clase.
   */

  getPassengerListUpgrade(reservation: Reservation) {
    const data = ApiMapper.mapPassengerListUpgrade(reservation);
    data.departureDate = this.formatDate(data.departureDate);
    const params = new HttpParams({ fromObject: { ...data } });
    let headers = new HttpHeaders({
      channel: environment.channel,
      flow: 'checkin',
      'x-transaction-id': reservation.flightSummary.cartId,
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    });
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this._http
      .get<PassengerListStandbyInt>(
        `${environment.urlApiCheckinBFF}${environment.versionCheckin}/kiosk/checkin/passengerListUpgrade${environment.versionPassengerList}`,
        {
          headers,
          params,
        }
      )
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
          return res;
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FOOTER_LOADING))
      );
  }

  formatDate(date) {
    const response = moment(date).format('YYYY-MM-DD');
    return response;
  }

  /**
   * Obtiene los documentos requeridos para el proceso de check-in de una reservación.
   *
   * @param {Reservation} reservation - Información de la reservación del vuelo.
   * @returns {Observable<any>} - Un observable que emite la información de los documentos requeridos.
   */

  getRequireDocuments(reservation: Reservation) {
    const body = ApiMapper.requestRequireDocuments(reservation);
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this._http
      .post(
        `${environment.urlApiCheckinBFF}${environment.versionCheckin}/kiosk/checkin/checkInDocuments/getViewDocuments`,
        body,
        {
          headers: {
            channel: environment.channel,
            flow: 'checkin',
            cacheEnabled: 'false',
            'x-transaction-id': reservation.flightSummary.cartId,
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
          },
        }
      )
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
          return DocumentsMapper.responseUpdateDocuments(res);
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FOOTER_LOADING))
      );
  }

  /**
   * Obtiene la lista de pasajeros en lista de espera (standby) para un vuelo específico.
   *
   * @param {Reservation} reservation - Información de la reservación del vuelo.
   * @returns {Observable<PassengerListStandbyInt>} - Un observable que emite la lista de pasajeros en espera.
   */

  getPassengerListstandby(reservation: Reservation) {
    const data = ApiMapper.requestListstandby(reservation);
    const params = new HttpParams({ fromObject: { ...data } });
    let headers = new HttpHeaders({
      channel: environment.channel,
      flow: 'checkin',
      'x-transaction-id': reservation.flightSummary.cartId,
      'Content-Type': 'application/json',
    });
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this._http
      .get<PassengerListStandbyInt>(
        `${environment.urlApiCheckinBFF}${environment.versionCheckin}/kiosk/checkin/passengerlist${environment.versionPassengerList}/standby`,
        {
          headers,
          params,
        }
      )
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
          return res;
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FOOTER_LOADING))
      );
  }

  /**
   * Obtiene un token de autenticación para el servicio de contactabilidad.
   *
   * @returns {Promise<ContactabilityTokenInt>} - Una promesa que resuelve con el token de autenticación.
   */

  async contactabilityToken() {
    const data = {
      apiName: environment.apiNameContactability,
      // feature/CI-21932-EliminarSecretContactability - valor inyectado por CI/CD via environment
      secret: environment.contactabilityAuth,
    };
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    const token$ = this._http
      .post<ContactabilityTokenInt>(`${this.urlApiContactabilityAuth}/auth`, data)
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
          return res;
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FOOTER_LOADING))
      );
    return await lastValueFrom<ContactabilityTokenInt>(token$);
  }

  /**
   * Envía una solicitud al servicio de contactabilidad digital para una reserva específica.
   *
   * @param {Reservation} reservation - Objeto de reserva que contiene los datos necesarios para la solicitud.
   * @returns {Promise<ContactabilityDigitalInt>} - Una promesa que resuelve con la respuesta del servicio de contactabilidad digital.
   */

  async contactabilityDigital(reservation: Reservation): Promise<ContactabilityDigitalInt> {
    const token = await this.contactabilityToken();
    const body = ApiMapper.mapContactabilityDigital(reservation);
    let headers = new HttpHeaders({
      Authorization: `Bearer ${token.message}`,
    });
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return new Promise((resolve, reject) => {
      this._http
        .post<ContactabilityDigitalInt>(`${this.urlApiContactability}/digital`, body, { headers })
        .subscribe({
          next: (response) => {
            return resolve(response);
          },
          error: (error) => {
            this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
            return reject(error);
          },
          complete: () => {
            this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
          },
        });
    });
  }

  /**
   * Obtiene los detalles del vuelo para una reserva específica.
   *
   * @param {Reservation} reservation - Objeto de reserva que contiene la información necesaria para la solicitud.
   * @returns {Observable<any>} - Un observable que emite la respuesta con los detalles del vuelo.
   */

  getFlightDetails(reservation: Reservation) {
    const body = ApiMapper.requestFlightDetails(reservation);
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this._http
      .post(
        `${environment.urlApiCheckinBFF}${environment.versionCheckin}/kiosk/checkin/flightDetails`,
        body,
        {
          headers: {
            channel: environment.channel,
            flow: 'checkin',
            cacheEnabled: 'false',
            'x-transaction-id': reservation.flightSummary.cartId,
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
          },
        }
      )
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
          return res;
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FOOTER_LOADING))
      );
  }

  /**
   * Manejo de errores HTTP para las peticiones.
   * @param {HttpErrorResponse} error - Respuesta de error HTTP
   * @returns {Observable<never>} Observable con error procesado
   */

  handlerError(
    error: HttpErrorResponse,
    TypeLoadingShow: TypeLoading = TypeLoading.FOOTER_LOADING
  ) {
    this._loadingService.hideLoading(TypeLoadingShow);

    const adapter = ErrorAdapter.adapt(error?.error || error);
    return throwError(adapter);
  }

  /**
   * Solicita la reimpresión de una tarjeta de  (boarding pass) para una reserva.
   *
   * Esta función toma un objeto de tipo `reservation`, genera el cuerpo de la solicitud
   * usando `ApiMapper`, y realiza una solicitud POST a un servicio externo para obtener
   * la tarjeta de embarque. Durante el proceso, muestra un indicador de carga y maneja
   * cualquier error que pueda ocurrir en la solicitud HTTP.
   *
   * @param {Reservation} reservation - La reserva para la cual se solicita la reimpresión de la tarjeta de embarque.
   *
   * @returns {Observable<any>} Un observable que emite la respuesta del servicio con la tarjeta de embarque.
   * En caso de error, se maneja mediante un interceptor de errores.
   */

  getReprintBP(reservation) {
    const body = ApiMapper.requestBoardingPass(reservation);

    let headers = new HttpHeaders({
      channel: environment.channel,
      flow: 'checkin',
      'x-transaction-id': reservation.flightSummary.cartId,
    });

    this._loadingService.showLoading(TypeLoading.COMPONENT_LOADING);

    return this._http
      .post<any>(
        `${environment.urlApiCheckinBFF}${environment.versionCheckin}/kiosk/checkin/getBoardingPass`,
        body,
        { headers }
      )
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.COMPONENT_LOADING);
          return ApiMapper.respBoardingPass(res);
        }),
        catchError((error) => this.handlerError(error, TypeLoading.COMPONENT_LOADING))
      );
  }
  /**
   * Realiza un proceso de check-in para una reserva.
   *
   * Esta función toma un objeto de tipo `Reservation`, genera el cuerpo de la solicitud
   * usando un `ApiMapper`, y realiza una solicitud POST a un servicio externo para
   * realizar el check-in de la reserva. Durante el proceso, muestra un indicador de carga
   * y maneja los errores que puedan ocurrir en la solicitud HTTP.
   *
   * @param {Reservation} reservation - La reserva para la cual se va a realizar el check-in.
   *
   * @returns {Observable<CheckinPecTabInt>} Un observable que emite la respuesta del check-in
   * tras recibir la respuesta del servidor. En caso de error, se maneja mediante un
   * interceptor de errores.
   */
  doCheckIn(reservation: Reservation, reprintBp: boolean) {
    const body = ApiMapper.requestPecTab(reservation, reprintBp);
    let headers = new HttpHeaders({
      channel: environment.channel,
      flow: 'checkin',
      cacheEnabled: 'false',
      'x-transaction-id': reservation.flightSummary.cartId,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    });
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);

    return this._http
      .post<CheckinPecTabInt>(
        `${environment.urlApiCheckinBFF}${environment.versionCheckin}/kiosk/checkin/docheckin`,
        body,
        { headers }
      )
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
          return ApiMapper.respDoCheckIn(res);
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FOOTER_LOADING))
      );
  }
}
